import React, { useEffect, useState } from 'react';
import API from '../api';
import { toast } from 'react-hot-toast';

export default function WIMManagement() {
  const [summary, setSummary] = useState(null);
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calc, setCalc] = useState({ vehicle_type: '', axle_count: '', gross_weight_kg: '', legal_weight_kg: '' });
  const [calcResult, setCalcResult] = useState(null);
  const [vioFilter, setVioFilter] = useState('all');

  const loadViolations = React.useCallback(async (filter = 'all') => {
    const params = {};
    if (filter !== 'all') params.status = filter === 'unpaid' ? 'unpaid' : 'paid';
    const res = await API.get('/wim/violations', { params });
    setViolations(res.data);
  }, []);

  const loadAll = React.useCallback(async () => {
    try {
      const s = await API.get('/wim/summary');
      setSummary(s.data);
      await loadViolations(vioFilter);
    } catch (e) {
      console.error(e);
      toast.error('ດຶງຂໍ້ມູນ WIM ບໍ່ສໍາເລັດ');
    } finally {
      setLoading(false);
    }
  }, [loadViolations, vioFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const payViolation = async (id) => {
    const method = window.prompt('ຊໍາລະດ້ວຍ (mifare/obu):', 'mifare');
    if (!method) return;
    let body = { method };
    if (method === 'mifare') {
      const card_uid = window.prompt('ໃສ່ເລກ (card_uid):');
      if (!card_uid) return; body.card_uid = card_uid;
    } else if (method === 'obu') {
      const device_id = window.prompt('ໃສ່ລະຫັດ OBU (device_id):');
      if (!device_id) return; body.device_id = device_id;
    } else {
      toast.error('ວິທີການບໍ່ຖືກຕ້ອງ');
      return;
    }
    try {
      await API.post(`/wim/violations/${id}/pay`, body);
      toast.success('ຊໍາລະເງິນສໍາເລັດ');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'ຊໍາລະເງິນບໍ່ສໍາເລັດ');
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">⚖️ ລະບົບ WIM (Weigh-In-Motion)</h1>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border">
          <div className="text-gray-500">ຈໍານວນລົດນ້ໍາໜັກເກີນ</div>
          <div className="text-2xl font-bold">{summary?.totals?.total_violations || 0}</div>
        </div>
        <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border">
          <div className="text-gray-500">ຍອດຄ່າປັບລວມ</div>
          <div className="text-2xl font-bold">{(summary?.totals?.total_fines || 0).toLocaleString()} LAK</div>
        </div>
        <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border">
          <div className="text-gray-500">ຍອດຄ້າງຊໍາລະ</div>
          <div className="text-2xl font-bold text-red-600">{(summary?.totals?.unpaid_amount || 0).toLocaleString()} LAK</div>
        </div>
      </div>

      {/* ລາຍການການຝ່າຝືນ (ພ້ອມແທັບ) */}
      <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold">ລາຍການການຝ່າຝືນ</h2>
          <div className="flex gap-2">
            {['all', 'unpaid', 'paid'].map(f => (
              <button key={f} onClick={async () => { setVioFilter(f); await loadViolations(f); }} className={`px-4 py-1 rounded ${vioFilter === f ? (f === 'paid' ? 'bg-green-600' : 'bg-blue-600') + ' text-white' : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200'}`}>
                {f === 'all' ? 'ທັງໝົດ' : f === 'unpaid' ? 'ຄ້າງຊໍາລະ' : 'ຈ່າຍແລ້ວ'}
              </button>
            ))}
            <button onClick={async () => { await loadViolations(vioFilter); }} className="bg-indigo-600 hover:bg-indigo-700 text-white py-1 px-3 rounded">ໂຫຼດໃໝ່</button>
          </div>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="p-2">ID</th>
                <th className="p-2">ທະບຽນ</th>
                <th className="p-2">ວັນ/ເວລາ</th>
                <th className="p-2">ສະຖານທີ່</th>
                <th className="p-2">ນ້ໍາໜັກ</th>
                <th className="p-2">ນ້ໍາໜັກເກີນ</th>
                <th className="p-2">ຄ່າປັບ</th>
                <th className="p-2">ສະຖານະ</th>
                <th className="p-2">ການດໍາເນີນການ</th>
              </tr>
            </thead>
            <tbody>
              {violations.length === 0 ? (
                <tr><td colSpan={6} className="p-4 text-center text-gray-500">ບໍ່ມີຂໍ້ມູນ</td></tr>
              ) : (
                violations.map((v, idx) => (
                  <tr key={v.id || idx} className="border-b">
                    <td className="p-2">{v.id}</td>
                    <td className="p-2">{v.license_plate || '-'}</td>
                    <td className="p-2">{v.event_time ? new Date(v.event_time).toLocaleString('lo-LA') : '-'}</td>
                    <td className="p-2">{v.location || '-'}</td>
                    <td className="p-2">{Number((v.gross_weight_kg || 0)).toLocaleString()} kg</td>
                    <td className="p-2 text-red-600 font-semibold">+{Number((v.event_overweight || v.overweight_kg || 0)).toLocaleString()} kg</td>
                    <td className="p-2 font-semibold">{Number(v.fine_amount || 0).toLocaleString()} LAK</td>
                    <td className="p-2">
                      {v.status === 'paid' ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">ຈ່າຍແລ້ວ</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-700">ຄ້າງຊໍາລະ</span>
                      )}
                    </td>
                    <td className="p-2">
                      {v.status !== 'paid' ? (
                        <button onClick={() => payViolation(v.id)} className="bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded">ຈ່າຍ</button>
                      ) : (
                        <span className="text-green-600">✓ ຈ່າຍແລ້ວ</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fine calculator using fine_tiers */}
      <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border">
        <h2 className="text-xl font-semibold mb-3">ຄໍານວນຄ່າປັບຈາກຕາຕະລາງ fine_tiers</h2>
        <form onSubmit={async (e) => { e.preventDefault(); try { const res = await API.get('/wim/compute', { params: { ...calc } }); setCalcResult(res.data); } catch (e) { toast.error('ຄໍານວນບໍ່ສໍາເລັດ'); } }} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="p-2 border rounded" placeholder="ປະເພດລົດ (ເຊັ່ນ truck)" value={calc.vehicle_type} onChange={e => setCalc({ ...calc, vehicle_type: e.target.value })} />
          <input className="p-2 border rounded" placeholder="ຈໍານວນເພົາ" value={calc.axle_count} onChange={e => setCalc({ ...calc, axle_count: e.target.value })} />
          <input className="p-2 border rounded" placeholder="ນ້ໍາໜັກລວມ (ກກ.)" value={calc.gross_weight_kg} onChange={e => setCalc({ ...calc, gross_weight_kg: e.target.value })} />
          <input className="p-2 border rounded" placeholder="ນ້ໍາໜັກຕາມກົດໝາຍ (ກກ.)" value={calc.legal_weight_kg} onChange={e => setCalc({ ...calc, legal_weight_kg: e.target.value })} />
          <div className="md:col-span-4">
            <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded">ຄໍານວນ</button>
          </div>
        </form>
        {calcResult && (
          <div className="mt-4 p-3 border rounded">
            <div>ນ້ໍາໜັກເກີນ: <strong>{calcResult.overweight_kg}</strong> ກກ.</div>
            <div>ຄ່າປັບທີ່ຄໍານວນໄດ້: <strong>{Number(calcResult.amount).toLocaleString()} LAK</strong></div>
            <div>ກົດທີ່ໃຊ້: <strong>{calcResult.tier?.name || '—'}</strong> ({calcResult.tier?.calculation || '-'})</div>
          </div>
        )}
      </div>

    </div>
  );
}
